import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Mail } from 'lucide-react';
import CampaignHistoryPanel from '../components/CampaignHistoryPanel';
import CampaignTabNav from '../components/CampaignTabNav';
import ClaimEmailCampaignPanel from '../components/ClaimEmailCampaignPanel';
import SponsorInviteCampaignPanel from '../components/SponsorInviteCampaignPanel';
import { CAMPAIGN_TABS, type CampaignTabId } from '../constants/campaignTabs';
import {
  CLAIM_CAMPAIGN_PAGE_SIZE,
  type ClaimCampaignStatusFilter,
} from '../constants/claimEmailCampaign';
import { useAuth } from '../context/AuthContext';
import type { CDMOApplication } from '../lib/supabase';
import { dbService } from '../lib/supabase';

const SEARCH_DEBOUNCE_MS = 320;

/** Resolves `?tab=` against the known tabs, falling back to the default. */
function parseCampaignTab(raw: string | null): CampaignTabId {
  return CAMPAIGN_TABS.find((tab) => tab.id === raw)?.id ?? 'cdmo';
}

/**
 * Admin-only unified Campaign page (CDMO claim, sponsor invites, history).
 */
const CampaignPage: React.FC = () => {
  const { isAdmin, adminCheckLoading, adminStatusResolved, user, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseCampaignTab(searchParams.get('tab'));

  const [cdmoData, setCdmoData] = useState<CDMOApplication[]>([]);
  const [totalEligibleCount, setTotalEligibleCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [campaignStatusFilter, setCampaignStatusFilter] =
    useState<ClaimCampaignStatusFilter>('all');
  const [cdmoLoading, setCdmoLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [cdmoError, setCdmoError] = useState<string | null>(null);
  const hasLoadedOnceRef = useRef(false);
  const prevDebouncedSearchRef = useRef<string | undefined>(undefined);
  const setTab = useCallback(
    (next: CampaignTabId) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === 'cdmo') {
            params.delete('tab');
          } else {
            params.set('tab', next);
          }
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (
      prevDebouncedSearchRef.current !== undefined &&
      prevDebouncedSearchRef.current !== debouncedSearch
    ) {
      setPage(1);
    }
    prevDebouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  const handleCampaignStatusFilterChange = useCallback((value: ClaimCampaignStatusFilter) => {
    setPage(1);
    setCampaignStatusFilter(value);
  }, []);

  const fetchCampaignPage = useCallback(async () => {
    if (!hasLoadedOnceRef.current) {
      setCdmoLoading(true);
    } else {
      setTableLoading(true);
    }
    setCdmoError(null);
    try {
      const { data, totalCount } = await dbService.getClaimCampaignEligibleCdmosPaginated({
        page,
        pageSize: CLAIM_CAMPAIGN_PAGE_SIZE,
        search: debouncedSearch,
        campaignStatus: campaignStatusFilter,
      });
      setCdmoData(data);
      setTotalEligibleCount(totalCount);
      const maxPage = Math.max(1, Math.ceil(totalCount / CLAIM_CAMPAIGN_PAGE_SIZE));
      setPage((p) => (p > maxPage ? maxPage : p));
      hasLoadedOnceRef.current = true;
    } catch (err) {
      console.error('CampaignPage load CDMOs:', err);
      setCdmoError('Failed to load CDMO profiles. Please try again.');
    } finally {
      setCdmoLoading(false);
      setTableLoading(false);
    }
  }, [page, debouncedSearch, campaignStatusFilter]);

  useEffect(() => {
    if (!adminStatusResolved || adminCheckLoading || !isAdmin || tab !== 'cdmo') {
      if (adminStatusResolved && !adminCheckLoading && !isAdmin) {
        setCdmoLoading(false);
      }
      return;
    }
    void fetchCampaignPage();
  }, [adminStatusResolved, adminCheckLoading, isAdmin, tab, fetchCampaignPage]);

  const awaitingAdminDecision =
    authLoading || (Boolean(user?.email) && !adminStatusResolved);

  if (awaitingAdminDecision || adminCheckLoading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 text-center border border-white/50 max-w-md w-full"
        >
          <Loader2 className="w-12 h-12 text-electric-blue mx-auto mb-4 animate-spin" />
          <p className="text-slate-gray">Checking access…</p>
        </motion.div>
      </div>
    );
  }

  if (!authLoading && adminStatusResolved && !isAdmin) {
    return (
      <Navigate
        to="/unauthorized?reason=campaign"
        replace
        state={{
          message:
            'Campaign management is limited to administrators. Contact support if you believe you should have access.',
        }}
      />
    );
  }

  const renderCdmoTab = () => {
    if (cdmoLoading && !hasLoadedOnceRef.current) {
      return (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-electric-blue" aria-hidden />
        </div>
      );
    }

    if (cdmoError) {
      return (
        <div className="py-8 text-center">
          <p className="text-charcoal font-medium mb-4">{cdmoError}</p>
          <button
            type="button"
            onClick={() => {
              void fetchCampaignPage();
            }}
            className="px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl"
          >
            Retry
          </button>
        </div>
      );
    }

    return (
      <ClaimEmailCampaignPanel
        cdmoData={cdmoData}
        onRefresh={fetchCampaignPage}
        embedded
        serverMode={{
          totalEligibleCount,
          page,
          pageSize: CLAIM_CAMPAIGN_PAGE_SIZE,
          onPageChange: setPage,
          searchQuery: searchInput,
          onSearchQueryChange: setSearchInput,
          campaignStatusFilter,
          onCampaignStatusFilterChange: handleCampaignStatusFilterChange,
          appliedSearchQuery: debouncedSearch,
          isLoadingRows: tableLoading,
        }}
      />
    );
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-light-gray to-sky-blue/20">
      <div className="w-full max-w-[min(100%,96rem)] mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col min-h-[calc(100vh-5rem)]">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-electric-blue/20 bg-white shadow-sm flex flex-col flex-1 min-h-0 p-6 sm:p-8"
        >
          <header className="mb-4 pb-4 border-b border-charcoal/10 shrink-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-charcoal flex items-center gap-3">
              <Mail className="w-8 h-8 text-electric-blue shrink-0" aria-hidden />
              Campaign
            </h1>
            <p className="mt-2 text-sm text-slate-gray leading-relaxed max-w-3xl">
              Manage CDMO claim invitations, sponsor invite sequences, and campaign history in one place.
            </p>
          </header>

          <CampaignTabNav active={tab} onChange={setTab} />

          <div className="flex flex-col flex-1 min-h-0" role="tabpanel">
            {tab === 'cdmo' && renderCdmoTab()}
            {tab === 'sponsor' && <SponsorInviteCampaignPanel embedded />}
            {tab === 'history' && <CampaignHistoryPanel embedded />}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CampaignPage;
